import { isDefined } from "@clipboard-health/util-ts";
import { addMinutes, differenceInMinutes, parseISO } from "date-fns";

/**
 * Computes the effective scheduled end date for a shift.
 *
 * For the `cap-at-shift-length` feature flag state, the scheduled end is based on when
 * the worker actually clocked in plus the original shift duration (shift.end - shift.start).
 * This ensures late clock-ins still get the full scheduled duration.
 *
 * For all other states, the scheduled end is the original shift end.
 */
export function getScheduledEndDate(params: {
  shiftStartDate: Date;
  shiftEndDate: Date;
  clockInDate: Date | undefined;
  isCapAtShiftLength: boolean;
}): Date {
  const { shiftStartDate, shiftEndDate, clockInDate, isCapAtShiftLength } = params;

  if (!isCapAtShiftLength || !clockInDate) {
    return shiftEndDate;
  }

  const shiftDurationInMinutes = differenceInMinutes(shiftEndDate, shiftStartDate);
  return addMinutes(clockInDate, shiftDurationInMinutes);
}

/**
 * Convenience wrapper around {@link getScheduledEndDate} that accepts a shift object
 * directly, handling the `parseISO` conversions internally.
 */
export function getScheduledEndDateFromShift(
  shift: {
    start: string;
    end: string;
    clockInOut?: { start?: string | null } | null;
  },
  isCapAtShiftLength: boolean
): Date {
  return getScheduledEndDate({
    shiftStartDate: parseISO(shift.start),
    shiftEndDate: parseISO(shift.end),
    clockInDate: isDefined(shift.clockInOut?.start) ? parseISO(shift.clockInOut.start) : undefined,
    isCapAtShiftLength,
  });
}
